import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useProjectContext } from "../contexts/ProjectContext";
import {
  ProjectSetupModal,
  parseSetupData,
  getPhaseLabel,
} from "../components/ProjectSetupModal";
import { formatCurrency, getImportoStimato } from "../utils/pricing";
import { parseCashPosition } from "../services/cashPosition";
import type { Preventivo, Lavoro } from "../types";
import { generateAllRateScadenze } from "../services/financeCalculations";
import { formatDate } from "../services/cashFlowCalculations";
import { useTasks, useUpdateTask } from "../hooks/useTasks";
import { CashPositionCard } from "../components/FlussiCassa/CashPositionCard";

export function Dashboard() {
  const { currentProject } = useProjectContext();
  const [showSetupModal, setShowSetupModal] = useState(false);

  const { data: tasks = [] } = useTasks(currentProject?.id);
  const updateTask = useUpdateTask();

  // Task aperti, i più urgenti in cima: prima chi ha una scadenza, poi il resto
  const taskAperti = useMemo(() => {
    return tasks
      .filter((t) => !t.completata)
      .sort((a, b) => {
        const urgency =
          Number(b.titolo.startsWith("SUBITO")) -
          Number(a.titolo.startsWith("SUBITO"));
        if (urgency) return urgency;
        if (a.scadenza && b.scadenza)
          return a.scadenza.localeCompare(b.scadenza);
        if (a.scadenza) return -1;
        if (b.scadenza) return 1;
        return a.ordine - b.ordine;
      });
  }, [tasks]);

  // Parse setup data from project description
  const setupData = useMemo(() => {
    return parseSetupData(currentProject?.description);
  }, [currentProject?.description]);

  // Prossime scadenze (rate mutui/prestiti) — prime 5 future
  const prossimeScadenze = useMemo(() => {
    const oggi = new Date();
    return generateAllRateScadenze(
      currentProject?.finanziamenti ?? [],
      oggi,
    ).slice(0, 5);
  }, [currentProject]);

  // Check if project needs setup (no setup data)
  const needsSetup = currentProject && !setupData;

  // Auto-show setup modal for new projects
  useEffect(() => {
    if (needsSetup) {
      setShowSetupModal(true);
    }
  }, [needsSetup, currentProject?.id]);

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900">
          Nessun progetto selezionato
        </h2>
        <p className="mt-2 text-gray-600">
          Seleziona o crea un progetto per visualizzare la dashboard.
        </p>
      </div>
    );
  }

  const immobile = currentProject.immobile;

  // Use immobile data if available, fall back to setupData
  const prezzoImmobile = immobile?.prezzoAcquisto
    ? parseFloat(immobile.prezzoAcquisto)
    : setupData?.property.price || 0;
  const hasCashPosition = !!parseCashPosition(currentProject.description);
  const suspended = new Set<string>(
    (currentProject.lavori ?? [])
      .filter((l: Lavoro) => l.attivo === false)
      .map((l: Lavoro) => l.id),
  );
  const budgetLavori = hasCashPosition
    ? (currentProject.preventivi ?? [])
        .filter(
          (p: Preventivo) =>
            p.attivo !== false && !(p.lavoroId && suspended.has(p.lavoroId)),
        )
        .reduce((sum: number, p: Preventivo) => sum + getImportoStimato(p), 0)
    : setupData?.renovation.budget || 0;
  const totaleInvestimento = prezzoImmobile + budgetLavori;

  return (
    <div className="space-y-6">
      {/* Setup Modal */}
      <ProjectSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        projectId={currentProject.id}
        projectName={currentProject.name}
      />

      {/* Header with Phase */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {currentProject.name}
            </h1>
            {setupData?.phase && (
              <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                {getPhaseLabel(setupData.phase)}
              </span>
            )}
          </div>
          {setupData?.notes && (
            <p className="mt-1 text-sm text-gray-600">{setupData.notes}</p>
          )}
          {!setupData && (
            <button
              onClick={() => setShowSetupModal(true)}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              + Configura progetto
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/immobile"
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-colors"
          >
            Scheda Immobile
          </Link>
          <button
            onClick={() => setShowSetupModal(true)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            Modifica
          </button>
        </div>
      </div>

      {/* Project Overview */}
      <CashPositionCard description={currentProject?.description} />
      {setupData || immobile ? (
        <>
          {/* Compact Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Prezzo Immobile
              </p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {prezzoImmobile ? formatCurrency(prezzoImmobile) : "-"}
              </p>
              {immobile?.tipoImmobile && (
                <p className="text-xs text-gray-500 mt-1">
                  {immobile.tipoImmobile}
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {hasCashPosition ? "Stima CME corrente" : "Budget Lavori"}
              </p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {budgetLavori ? formatCurrency(budgetLavori) : "-"}
              </p>
              {setupData?.renovation.type && (
                <p className="text-xs text-gray-500 mt-1">
                  {hasCashPosition
                    ? "Da completare e quotare"
                    : setupData.renovation.type}
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {hasCashPosition
                  ? "Acquisto + CME stimato"
                  : "Totale Investimento"}
              </p>
              <p className="text-xl font-bold text-primary-700 mt-1">
                {totaleInvestimento ? formatCurrency(totaleInvestimento) : "-"}
              </p>
              {immobile?.superficie && totaleInvestimento > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {formatCurrency(
                    totaleInvestimento / parseFloat(immobile.superficie),
                  )}
                  /mq
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Superficie
              </p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {immobile?.superficie
                  ? `${immobile.superficie} mq`
                  : setupData?.property.size
                    ? `${setupData.property.size} mq`
                    : "-"}
              </p>
              {immobile?.indirizzo && (
                <p
                  className="text-xs text-gray-500 mt-1 truncate"
                  title={immobile.indirizzo}
                >
                  {immobile.indirizzo}
                </p>
              )}
            </div>
          </div>

          {/* Secondary Info Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Prima Casa
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {immobile ? (immobile.primaCasa ? "Si" : "No") : "-"}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Data Rogito
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {immobile?.dataRogito
                  ? new Date(immobile.dataRogito).toLocaleDateString("it-IT")
                  : setupData?.timeline.possession_date
                    ? new Date(
                        setupData.timeline.possession_date,
                      ).toLocaleDateString("it-IT")
                    : "-"}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Classe Energetica
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {immobile?.classeEnergetica || "-"}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Finanziamento
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {setupData?.renovation.financing === "no" && "Contanti"}
                {setupData?.renovation.financing === "mutuo" && "Mutuo"}
                {setupData?.renovation.financing === "prestito" && "Prestito"}
                {setupData?.renovation.financing === "entrambi" &&
                  "Mutuo + Prestito"}
                {!setupData?.renovation.financing && "-"}
              </p>
            </div>
          </div>

          {/* Priorities */}
          {setupData?.priorities && setupData.priorities.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Priorita
              </h3>
              <div className="flex flex-wrap gap-2">
                {setupData.priorities.map((priority) => (
                  <span
                    key={priority}
                    className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
                  >
                    {priority}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Task da fare */}
          {taskAperti.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  ✅ Da fare
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    {taskAperti.length}
                  </span>
                </h3>
                <Link
                  to="/task"
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Vedi tutti →
                </Link>
              </div>
              <div className="divide-y divide-gray-100">
                {taskAperti.slice(0, 5).map((t) => {
                  const scaduta =
                    !!t.scadenza &&
                    new Date(t.scadenza).setHours(0, 0, 0, 0) <
                      new Date().setHours(0, 0, 0, 0);
                  return (
                    <div key={t.id} className="flex items-start gap-3 py-2">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() =>
                          updateTask.mutate({
                            id: t.id,
                            projectId: currentProject!.id,
                            data: { completata: true },
                          })
                        }
                        className="mt-0.5 w-4 h-4 shrink-0"
                        aria-label={`Segna come completato: ${t.titolo}`}
                      />
                      <Link to="/task" className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900 truncate">
                          {t.titolo}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {t.scadenza && (
                            <span
                              className={`mr-2 ${scaduta ? "text-red-600 font-medium" : ""}`}
                            >
                              entro{" "}
                              {new Date(t.scadenza).toLocaleDateString("it-IT")}
                            </span>
                          )}
                          {t.ganttTask && (
                            <span className="text-amber-700">
                              ⛓ {t.ganttTask.nome}
                            </span>
                          )}
                        </p>
                      </Link>
                    </div>
                  );
                })}
              </div>
              {taskAperti.length > 5 && (
                <p className="text-xs text-gray-400 mt-2">
                  e altri {taskAperti.length - 5}
                </p>
              )}
            </div>
          )}

          {/* Prossime scadenze */}
          {prossimeScadenze.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  🔔 Prossime scadenze
                </h3>
                <Link
                  to="/scadenze"
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Vedi tutte →
                </Link>
              </div>
              <div className="divide-y divide-gray-100">
                {prossimeScadenze.map((r) => (
                  <div
                    key={`${r.finanziamentoId}-${r.numero}`}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-semibold shrink-0">
                        {new Date(r.date).getUTCDate()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900">
                          {formatDate(r.date)}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {r.finanziamentoNome} · rata {r.numero}/{r.totaleRate}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 currency">
                      {formatCurrency(r.importo)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Azioni Rapide
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Link
                to="/immobile"
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
              >
                <span className="text-xl">🏠</span>
                <p className="text-xs font-medium mt-1">Immobile</p>
              </Link>
              <Link
                to="/finanziamento"
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
              >
                <span className="text-xl">💰</span>
                <p className="text-xs font-medium mt-1">Finanziamento</p>
              </Link>
              <Link
                to="/preventivi"
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
              >
                <span className="text-xl">📋</span>
                <p className="text-xs font-medium mt-1">Preventivi</p>
              </Link>
              <Link
                to="/gantt"
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
              >
                <span className="text-xl">📅</span>
                <p className="text-xs font-medium mt-1">Gantt</p>
              </Link>
              <Link
                to="/flussi-cassa"
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
              >
                <span className="text-xl">📈</span>
                <p className="text-xs font-medium mt-1">Flussi Cassa</p>
              </Link>
            </div>
          </div>
        </>
      ) : (
        /* Empty state when no setup */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
          <div className="text-6xl mb-4">🏠</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Configura il tuo progetto
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Inserisci le informazioni base del tuo progetto per iniziare a
            tenere traccia di tutto.
          </p>
          <div className="flex justify-center gap-3">
            <Link to="/immobile" className="btn-primary px-6 py-3">
              Compila Scheda Immobile
            </Link>
            <button
              onClick={() => setShowSetupModal(true)}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Configurazione rapida
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
